import { useState } from 'react';
import { PageHeader } from '../components/Layout.jsx';
import { Alert, Card, Empty, ErrorState, Input, Loading, Select, Stat, Textarea } from '../components/ui.jsx';
import { Modal, ConfirmModal } from '../components/Modal.jsx';
import { IconPlus, IconWallet } from '../components/Icons.jsx';
import { api } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { useAuth } from '../lib/auth.jsx';
import { useToast } from '../lib/toast.jsx';
import { t } from '../lib/i18n.js';
import { formatDate, money, monthLabel, num, startOfMonth, today } from '../lib/format.js';

export default function Payroll() {
  const toast = useToast();
  const { isAdmin } = useAuth();
  const [month, setMonth] = useState(today().slice(0, 7));
  const [editing, setEditing] = useState(null);
  const [paying, setPaying] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const staff = useApi('/api/staff', { include_inactive: true });
  const status = useApi('/api/staff/payroll-status', { month: `${month}-01` });
  const payments = useApi('/api/staff/payments', { limit: 50 });

  const unpaid = (status.data?.rows || []).filter((row) => !row.has_payment);
  const dueIqd = unpaid.filter((r) => r.salary_currency === 'IQD').reduce((sum, r) => sum + Number(r.monthly_salary), 0);
  const dueUsd = unpaid.filter((r) => r.salary_currency === 'USD').reduce((sum, r) => sum + Number(r.monthly_salary), 0);

  const reloadAll = () => {
    staff.reload();
    status.reload();
    payments.reload();
  };

  const removeStaff = async () => {
    setBusy(true);
    try {
      await api.del(`/api/staff/${deleting.id}`);
      toast.success(t.payroll.staffDeleted);
      setDeleting(null);
      reloadAll();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title={t.payroll.title}
        subtitle={t.payroll.subtitle}
        actions={
          <button type="button" className="btn btn-sm btn-primary no-print" onClick={() => setEditing({})}>
            <IconPlus /> {t.common.add}
          </button>
        }
      />

      <div className="page">
        <Card>
          <Input
            label={t.payroll.month}
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            hint={monthLabel(`${month}-01`)}
          />
        </Card>

        <div className="stat-grid">
          <Stat
            label={t.payroll.staffCount}
            value={num((staff.data || []).filter((s) => s.active).length)}
            hint={t.common.active}
          />
          <Stat
            label={t.payroll.unpaidThisMonth}
            value={num(unpaid.length)}
            hint={`${money(dueIqd, 'IQD')} · ${money(dueUsd, 'USD')}`}
            tone={unpaid.length ? 'danger' : 'success'}
          />
        </div>

        <ErrorState error={status.error} onRetry={status.reload} />
        {status.loading && !status.data && <Loading />}

        <Card title={t.payroll.monthStatus(monthLabel(`${month}-01`))}>
          {(status.data?.rows || []).length === 0 ? (
            <Empty title={t.payroll.noStaff} message={t.payroll.noStaffHint} />
          ) : (
            <div className="record-list">
              {status.data.rows.map((row) => (
                <div key={row.id} className="record">
                  <div className="record-top">
                    <span className="title">{row.name}</span>
                    <span className={`amount ${row.has_payment ? 'pos' : ''}`}>
                      {row.has_payment
                        ? money(row.salary_currency === 'IQD' ? row.paid_iqd : row.paid_usd, row.salary_currency)
                        : money(row.monthly_salary, row.salary_currency)}
                    </span>
                  </div>
                  <div className="record-meta">
                    {row.role && <span>{row.role}</span>}
                    <span className={`badge ${row.has_payment ? 'cash' : 'debt'}`}>
                      {row.has_payment ? t.payroll.paid : t.payroll.unpaid}
                    </span>
                  </div>
                  <div className="btn-row no-print" style={{ marginTop: 4 }}>
                    <button type="button" className="btn btn-sm" onClick={() => setPaying({ ...row, month })}>
                      <IconWallet /> {t.payroll.recordPayment}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title={t.payroll.staff}>
          <ErrorState error={staff.error} onRetry={staff.reload} />
          {(staff.data || []).length === 0 && <Empty title={t.payroll.noStaffOnFile} />}
          {(staff.data || []).length > 0 && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t.common.name}</th>
                    <th>{t.payroll.role}</th>
                    <th className="num">{t.payroll.salary}</th>
                    <th>{t.payroll.hired}</th>
                    <th>{t.payroll.lastPaid}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(staff.data || []).map((row) => (
                    <tr key={row.id}>
                      <td>
                        {row.name}
                        {!row.active && (
                          <span className="badge muted" style={{ marginInlineStart: 6 }}>
                            {t.common.inactive}
                          </span>
                        )}
                      </td>
                      <td>{row.role || '-'}</td>
                      <td className="num">{money(row.monthly_salary, row.salary_currency)}</td>
                      <td>{row.hire_date ? formatDate(row.hire_date) : '-'}</td>
                      <td>{row.last_paid_on ? formatDate(row.last_paid_on) : t.common.never}</td>
                      <td>
                        <div className="btn-row">
                          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditing(row)}>
                            {t.common.edit}
                          </button>
                          {isAdmin && (
                            <button type="button" className="btn btn-sm btn-danger" onClick={() => setDeleting(row)}>
                              {t.common.delete}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title={t.payroll.recentPayments}>
          {(payments.data || []).length === 0 ? (
            <Empty title={t.payroll.noPayments} />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t.payroll.paidOn}</th>
                    <th>{t.payroll.staff}</th>
                    <th>{t.payroll.covers}</th>
                    <th className="num">{t.common.amount}</th>
                    <th>{t.common.notes}</th>
                  </tr>
                </thead>
                <tbody>
                  {(payments.data || []).map((row) => (
                    <tr key={row.id}>
                      <td>{formatDate(row.paid_on)}</td>
                      <td>{row.staff_name}</td>
                      <td>{monthLabel(row.period_month)}</td>
                      <td className="num">{money(row.amount, row.currency)}</td>
                      <td className="wrap">{row.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {editing && (
        <StaffForm
          member={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast.success(t.payroll.staffSaved);
            reloadAll();
          }}
        />
      )}

      {paying && (
        <SalaryPaymentForm
          member={paying}
          onClose={() => setPaying(null)}
          onSaved={() => {
            setPaying(null);
            toast.success(t.payroll.paymentSaved);
            reloadAll();
          }}
        />
      )}

      {deleting && (
        <ConfirmModal
          title={t.payroll.deleteStaff}
          message={t.payroll.deleteStaffMessage(deleting.name)}
          onConfirm={removeStaff}
          onClose={() => setDeleting(null)}
          busy={busy}
        />
      )}
    </>
  );
}

function StaffForm({ member, onClose, onSaved }) {
  const isEdit = Boolean(member.id);
  const [form, setForm] = useState({
    name: member.name || '',
    role: member.role || '',
    phone: member.phone || '',
    monthly_salary: member.monthly_salary || '',
    salary_currency: member.salary_currency || 'IQD',
    hire_date: member.hire_date || '',
    notes: member.notes || '',
    active: member.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (isEdit) await api.patch(`/api/staff/${member.id}`, form);
      else await api.post('/api/staff', form);
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={isEdit ? t.payroll.editStaff : t.payroll.newStaff}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            {t.common.cancel}
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={busy || !form.name.trim()}>
            {busy ? <span className="spinner" /> : t.common.save}
          </button>
        </>
      }
    >
      {error && <Alert tone="error">{error}</Alert>}
      <Input label={t.common.name} required value={form.name} onChange={(e) => set({ name: e.target.value })} />
      <Input
        label={t.payroll.role}
        value={form.role}
        onChange={(e) => set({ role: e.target.value })}
        placeholder={t.payroll.rolePlaceholder}
      />
      <Input
        label={t.common.phone}
        value={form.phone}
        onChange={(e) => set({ phone: e.target.value })}
        inputMode="tel"
        dir="ltr"
      />
      <div className="form-grid cols-2">
        <Input
          label={t.payroll.monthlySalary}
          inputMode="decimal"
          value={form.monthly_salary}
          onChange={(e) => set({ monthly_salary: e.target.value.replace(/[^\d.]/g, '') })}
        />
        <Select
          label={t.payroll.salaryCurrency}
          value={form.salary_currency}
          onChange={(e) => set({ salary_currency: e.target.value })}
          options={[
            { value: 'IQD', label: t.report.iqd },
            { value: 'USD', label: t.report.usd },
          ]}
        />
      </div>
      <Input
        label={t.payroll.hireDate}
        type="date"
        value={form.hire_date || ''}
        onChange={(e) => set({ hire_date: e.target.value })}
      />
      <Textarea label={t.common.notes} value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={2} />
      {isEdit && (
        <Select
          label={t.common.status}
          value={form.active ? 'true' : 'false'}
          onChange={(e) => set({ active: e.target.value === 'true' })}
          options={[
            { value: 'true', label: t.common.active },
            { value: 'false', label: t.common.inactive },
          ]}
        />
      )}
    </Modal>
  );
}

function SalaryPaymentForm({ member, onClose, onSaved }) {
  const [form, setForm] = useState({
    paid_on: today(),
    period_month: `${member.month || startOfMonth().slice(0, 7)}-01`,
    amount: member.monthly_salary || '',
    currency: member.salary_currency || 'IQD',
    notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/staff/${member.id}/payments`, form, {
        queueOffline: true,
        label: `${t.payroll.salary} — ${member.name}`,
      });
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={t.payroll.payStaff(member.name)}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            {t.common.cancel}
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={busy || !(Number(form.amount) > 0)}>
            {busy ? <span className="spinner" /> : t.payroll.recordPayment}
          </button>
        </>
      }
    >
      {error && <Alert tone="error">{error}</Alert>}
      <Input
        label={t.payroll.paidOn}
        required
        type="date"
        value={form.paid_on}
        onChange={(e) => set({ paid_on: e.target.value })}
      />
      <Input
        label={t.payroll.monthCovered}
        required
        type="month"
        value={form.period_month.slice(0, 7)}
        onChange={(e) => set({ period_month: `${e.target.value}-01` })}
        hint={t.payroll.monthCoveredHint}
      />
      <div className="form-grid cols-2">
        <Input
          label={t.common.amount}
          required
          inputMode="decimal"
          value={form.amount}
          onChange={(e) => set({ amount: e.target.value.replace(/[^\d.]/g, '') })}
        />
        <Select
          label={t.common.currency}
          value={form.currency}
          onChange={(e) => set({ currency: e.target.value })}
          options={[
            { value: 'IQD', label: t.report.iqd },
            { value: 'USD', label: t.report.usd },
          ]}
        />
      </div>
      <Textarea label={t.common.notes} value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={2} />
    </Modal>
  );
}
